import { Controller } from "@hotwired/stimulus"

// Stimulus controller for managing lesson skills in forms
// Handles dynamic addition/removal of skill fields and autosave functionality
export default class extends Controller {
  static targets = ["skillField", "container", "durationDisplay", "startTimeField", "endTimeField", "timerStatus"]
  static values = {
    autosaveUrl: String,
    autosaveInterval: { type: Number, default: 5000 } // 5 seconds
  }

  connect() {
    console.log("Lesson skills controller connected")

    // Start autosave timer if URL is provided
    if (this.hasAutosaveUrlValue) {
      this.startAutosave()
    }
  }

  disconnect() {
    // Stop autosave when controller is disconnected
    this.stopAutosave()
  }

  // Add a new skill field to the form
  // Clones the template and appends it to the container
  addSkill(event) {
    event.preventDefault()

    const template = this.skillFieldTargets[0]
    if (!template) {
      console.error("No skill field template found")
      return
    }

    // Clone the template
    const clone = template.cloneNode(true)

    // Clear values in the clone
    const inputs = clone.querySelectorAll('input, select, textarea')
    inputs.forEach(input => {
      if (input.type === 'hidden' && input.name.includes('_destroy')) {
        input.value = '0'
      } else if (input.type !== 'hidden') {
        input.value = ''
      }
    })

    // Update field names to use a unique index
    const newIndex = new Date().getTime()
    const regexp = /\[lesson_skills_attributes\]\[\d+\]/g
    clone.innerHTML = clone.innerHTML.replace(regexp, `[lesson_skills_attributes][${newIndex}]`)

    // Append the clone to the container
    if (this.hasContainerTarget) {
      this.containerTarget.appendChild(clone)
    } else {
      template.parentElement.appendChild(clone)
    }
  }

  // Remove a skill field from the form
  // For existing records, marks for deletion; for new records, removes from DOM
  removeSkill(event) {
    event.preventDefault()

    const skillField = event.target.closest('[data-lesson-skills-target="skillField"]')
    if (!skillField) return

    // Check if this is an existing record (has an ID)
    const idField = skillField.querySelector('input[name*="[id]"]')

    if (idField && idField.value) {
      // Existing record: mark for deletion
      const destroyField = skillField.querySelector('input[name*="[_destroy]"]')
      if (destroyField) {
        destroyField.value = '1'
      }
      skillField.style.display = 'none'
    } else {
      // New record: just remove from DOM
      skillField.remove()
    }
  }

  // Increment frequency count for behavior tracking
  // Used for frequency recording during lessons
  incrementFrequency(event) {
    event.preventDefault()

    const button = event.currentTarget
    const skillField = button.closest('[data-lesson-skills-target="skillField"]')
    if (!skillField) return

    // Find the frequency_count input field
    const frequencyInput = skillField.querySelector('input[name*="[frequency_count]"]')
    if (!frequencyInput) return

    // Increment the value
    const currentValue = parseInt(frequencyInput.value) || 0
    frequencyInput.value = currentValue + 1

    // Visual feedback
    button.classList.add('btn-success')
    setTimeout(() => {
      button.classList.remove('btn-success')
    }, 200)
  }

  // Start behavior duration timer
  startTimer(event) {
    event.preventDefault()

    const button = event.currentTarget
    const skillField = button.closest('[data-lesson-skills-target="skillField"]')
    if (!skillField) return

    const startTimeField = skillField.querySelector('[data-lesson-skills-target="startTimeField"]')
    const endTimeField = skillField.querySelector('[data-lesson-skills-target="endTimeField"]')
    const durationDisplay = skillField.querySelector('[data-lesson-skills-target="durationDisplay"]')
    const timerStatus = skillField.querySelector('[data-lesson-skills-target="timerStatus"]')

    if (!startTimeField || !endTimeField || !durationDisplay) return

    // Set start time to now
    const now = new Date().toISOString()
    startTimeField.value = now
    endTimeField.value = ''

    // Update display
    durationDisplay.textContent = 'Timer running...'
    if (timerStatus) {
      timerStatus.textContent = 'Timer running...'
      timerStatus.classList.add('text-success')
    }

    // Visual feedback
    button.classList.add('btn-light')
    button.disabled = true

    // Start live timer update
    this.startLiveTimer(skillField)
  }

  // Stop behavior duration timer
  stopTimer(event) {
    event.preventDefault()

    const button = event.currentTarget
    const skillField = button.closest('[data-lesson-skills-target="skillField"]')
    if (!skillField) return

    const startTimeField = skillField.querySelector('[data-lesson-skills-target="startTimeField"]')
    const endTimeField = skillField.querySelector('[data-lesson-skills-target="endTimeField"]')
    const durationDisplay = skillField.querySelector('[data-lesson-skills-target="durationDisplay"]')
    const timerStatus = skillField.querySelector('[data-lesson-skills-target="timerStatus"]')

    if (!startTimeField || !endTimeField || !durationDisplay) return
    if (!startTimeField.value) return // No timer running

    // Set end time to now
    const now = new Date().toISOString()
    endTimeField.value = now

    // Calculate and display duration
    const startTime = new Date(startTimeField.value)
    const endTime = new Date(now)
    const duration = this.formatDuration((endTime - startTime) / 1000)

    durationDisplay.textContent = duration
    if (timerStatus) {
      timerStatus.textContent = `Duration: ${duration}`
      timerStatus.classList.remove('text-success')
      timerStatus.classList.add('text-info')
    }

    // Stop live timer
    this.stopLiveTimer(skillField)

    // Re-enable start button
    const startButton = skillField.querySelector('[data-action="lesson-skills#startTimer"]')
    if (startButton) {
      startButton.classList.remove('btn-light')
      startButton.disabled = false
    }
  }

  // Reset behavior duration timer
  resetTimer(event) {
    event.preventDefault()

    const button = event.currentTarget
    const skillField = button.closest('[data-lesson-skills-target="skillField"]')
    if (!skillField) return

    const startTimeField = skillField.querySelector('[data-lesson-skills-target="startTimeField"]')
    const endTimeField = skillField.querySelector('[data-lesson-skills-target="endTimeField"]')
    const durationDisplay = skillField.querySelector('[data-lesson-skills-target="durationDisplay"]')
    const timerStatus = skillField.querySelector('[data-lesson-skills-target="timerStatus"]')

    if (!startTimeField || !endTimeField || !durationDisplay) return

    // Clear timer values
    startTimeField.value = ''
    endTimeField.value = ''
    durationDisplay.textContent = 'N/A'
    if (timerStatus) {
      timerStatus.textContent = ''
      timerStatus.classList.remove('text-success', 'text-info')
    }

    // Stop live timer
    this.stopLiveTimer(skillField)

    // Re-enable start button
    const startButton = skillField.querySelector('[data-action="lesson-skills#startTimer"]')
    if (startButton) {
      startButton.classList.remove('btn-light')
      startButton.disabled = false
    }
  }

  // Start live timer update
  startLiveTimer(skillField) {
    const timerId = setInterval(() => {
      const startTimeField = skillField.querySelector('[data-lesson-skills-target="startTimeField"]')
      const durationDisplay = skillField.querySelector('[data-lesson-skills-target="durationDisplay"]')

      if (!startTimeField || !startTimeField.value) {
        clearInterval(timerId)
        return
      }

      const startTime = new Date(startTimeField.value)
      const now = new Date()
      const duration = this.formatDuration((now - startTime) / 1000)
      durationDisplay.textContent = duration
    }, 1000)

    // Store timer ID on the element so we can stop it later
    skillField.dataset.timerId = timerId
  }

  // Stop live timer update
  stopLiveTimer(skillField) {
    const timerId = skillField.dataset.timerId
    if (timerId) {
      clearInterval(parseInt(timerId))
      delete skillField.dataset.timerId
    }
  }

  // Format duration in seconds to human-readable string
  formatDuration(seconds) {
    seconds = Math.floor(seconds)
    if (seconds < 60) {
      return `${seconds}s`
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}m ${remainingSeconds}s`
    } else {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return `${hours}h ${minutes}m`
    }
  }

  // Start the autosave timer
  startAutosave() {
    this.autosaveTimer = setInterval(() => {
      this.performAutosave()
    }, this.autosaveIntervalValue)
  }

  // Stop the autosave timer
  stopAutosave() {
    if (this.autosaveTimer) {
      clearInterval(this.autosaveTimer)
      this.autosaveTimer = null
    }
  }

  // Perform autosave by submitting form data to the server
  async performAutosave() {
    const form = this.element.closest('form')
    if (!form) return

    const formData = new FormData(form)

    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        console.log('Autosave successful')
        this.showAutosaveStatus('saved')
      } else {
        console.error('Autosave failed:', response.statusText)
        this.showAutosaveStatus('error')
      }
    } catch (error) {
      console.error('Autosave error:', error)
      this.showAutosaveStatus('error')
    }
  }

  // Show autosave status to the user
  showAutosaveStatus(status) {
    // This method can be customized to show a visual indicator
    const statusElement = document.querySelector('[data-autosave-status]')
    if (statusElement) {
      statusElement.textContent = status === 'saved' ? 'Saved' : 'Error saving'
      statusElement.className = status === 'saved' ? 'text-success' : 'text-danger'
    }
  }
}
